import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import "./MegaMenu1.css";

import icon01 from "../../images/icon/service-icon01.svg";
import icon02 from "../../images/icon/service-icon02.svg";
import icon03 from "../../images/icon/service-icon03.svg";
import icon04 from "../../images/icon/service-icon04.svg";
import icon05 from "../../images/icon/service-icon05.svg";
import icon06 from "../../images/icon/service-icon06.svg";
import icon07 from "../../images/icon/service-icon07.svg";

export interface VerticalItem {
  icon: string;
  name: string;
  desc: string;
  to: string;
  isNew?: string;
}

// 20 industry verticals (alphabetically arranged A-Z)
const verticals: VerticalItem[] = [
  {
    icon: icon06,
    name: "Arena Gaming",
    desc: "Gaming stations, live billing, tournaments, loyalty, player rankings",
    to: "arena-gaming",
  },
  {
    icon: icon03,
    name: "Bike Dealer",
    desc: "Bike inventory, sales, financing, service booking, public storefront",
    to: "bike-dealer"
  },
  {
    icon: icon02,
    name: "Car Dealer",
    desc: "Vehicle inventory, sales, financing, service scheduling, storefront",
    to: "car-dealer"
  },
  {
    icon: icon03,
    name: "Construction Companies",
    desc: "BOQ estimation, tender quotes, CPWD rates, RA bills, measurements",
    to: "construction"
  },
  {
    icon: icon07,
    name: "Consulting X Advisory",
    desc: "Clients, proposals, contracts, time tracking, timesheets, billing",
    to: "consulting"
  },
  {
    icon: icon03,
    name: "Education",
    desc: "Admissions, fees, students, teachers, certificates, parent portal",
    to: "education"
  },
  {
    icon: icon05,
    name: "Gym X Fitness",
    desc: "Memberships, classes, trainers, body metrics, equipment, billing",
    to: "gym-fitness"
  },
  {
    icon: icon04,
    name: "Healthcare",
    desc: "Patient records, appointments, billing, prescriptions, telemedicine",
    to: "healthcare"
  },
  {
    icon: icon01,
    name: "Hotel PMS",
    desc: "Front desk, housekeeping, F&B, GST billing, payments, booking website",
    to: "hotel"
  },
  {
    icon: icon05,
    name: "IT Services X Software",
    desc: "Projects, managed services, helpdesk, SLA tracking, DevOps",
    to: "it-services",
  },
  {
    icon: icon05,
    name: "Logistics",
    desc: "Fleet management, route optimization, delivery tracking, driver app",
    to: "logistics"
  },
  {
    icon: icon05,
    name: "Manufacturing",
    desc: "Production costing, BOM generation, inventory tracking, quality control",
    to: "manufacturing"
  },
  {
    icon: icon01,
    name: "PG & Hostel Management",
    desc: "Bed inventory, monthly rent, sub-metered electricity, deposits, tenant portal",
    to: "pg-hostel-management-software",
  },
  {
    icon: icon07,
    name: "Real Estate",
    desc: "Property listings, leads, site visits, agreements, property storefront",
    to: "real-estate"
  },
  {
    icon: icon02,
    name: "Restaurant",
    desc: "Tables, orders, kitchen display, billing, inventory, public menu",
    to: "restaurant"
  },
  {
    icon: icon01,
    name: "Retail X E-commerce",
    desc: "POS, inventory, multi-store management, sales and analytics",
    to: "retail"
  },
  {
    icon: icon04,
    name: "Salon X Spa",
    desc: "Appointments, stylists, services, scheduling, billing, booking storefront",
    to: "salon-spa",
  },
  {
    icon: icon03,
    name: "Study Abroad",
    desc: "Students, universities, applications, visas, documents, commissions",
    to: "study-abroad",
  },
  {
    icon: icon06,
    name: "Tourism X Travel",
    desc: "Packages, itineraries, agent commissions, bookings, travel storefront",
    to: "travel"
  },
  {
    icon: icon06,
    name: "Trading X Distribution",
    desc: "Purchase orders, sales orders, inventory, GST invoicing, dispatch",
    to: "trading",
  }
];

interface MegaMenu1Props {
  onItemClick?: () => void;
}

const MegaMenu1: React.FC<MegaMenu1Props> = ({ onItemClick }) => {
  // Guarantee ascending alphabetical order by vertical name
  const sortedVerticals = useMemo(() => {
    return [...verticals].sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (onItemClick) {
      onItemClick();
    }
  };

  return (
    <div className="submenu">
      <div className="mega_menu_wrapper industries_mega_wrapper">
        <div className="container-fluid p-0">
          <div className="mega_menu_wrapper_inner industries_megamenu_inner">
            {/* Top Header Strip */}
            <div className="industries_mm_header">
              <div className="industries_mm_title_wrap">
                <span className="industries_mm_tag">
                  <i className="fas fa-layer-group"></i>
                  <span>Industries & Verticals</span>
                </span>
                <span className="industries_mm_sub">
                  20 specialized AI-powered operating systems built for your industry
                </span>
              </div>
              <Link
                to="/industries"
                className="industries_mm_view_all_link"
                onClick={handleLinkClick}
              >
                <span>Browse All</span>
                <i className="fas fa-arrow-right"></i>
              </Link>
            </div>

            {/* Compact 4-Column Grid (20 Verticals + 1 Action Tile = 21 cards, 6th row half-filled) */}
            <div className="industries_mm_grid">
              {sortedVerticals.map((vertical) => (
                <Link
                  key={vertical.to}
                  to={"/industry/" + vertical.to}
                  className="industries_mm_card"
                  title={vertical.desc}
                  onClick={handleLinkClick}
                >
                  <span className="industries_mm_icon_box">
                    <img src={vertical.icon} alt={vertical.name} />
                  </span>
                  <span className="industries_mm_content">
                    <span className="industries_mm_name">{vertical.name}</span>
                  </span>
                  {vertical.isNew ? (
                    <span className="industries_mm_badge">{vertical.isNew}</span>
                  ) : null}
                </Link>
              ))}

              {/* 21st Card: Direct Quick-Access to Complete Industries Catalog */}
              <Link
                to="/industries"
                className="industries_mm_card industries_mm_card_all"
                onClick={handleLinkClick}
              >
                <span className="industries_mm_icon_box">
                  <i className="fas fa-arrow-up-right-from-square"></i>
                </span>
                <span className="industries_mm_content">
                  <span className="industries_mm_name highlight">
                    View All 20+ Industries
                  </span>
                </span>
              </Link>
            </div>

            {/* Bottom Footer Strip */}
            <div className="industries_mm_footer">
              <div className="industries_mm_footer_info">
                <span className="industries_mm_footer_badge">CUSTOM ARCHITECTURE</span>
                <span>
                  Need an industry-specific ERP or custom AI agent workflow? We tailor business operating systems.
                </span>
              </div>
              <Link
                to="/contact"
                className="industries_mm_footer_cta"
                onClick={handleLinkClick}
              >
                <span>Talk to Solutions Architect</span>
                <i className="fas fa-arrow-right"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MegaMenu1;
